import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ExposeService } from '../services/expose-service';
import { SpinnerService } from 'src/app/all-modules/shared/services/spinner.service';
import { REPORT_LINK_STATUS, ReportLinkDocument, ReportLinkViewModel } from '../model/report-link.model';

/**
 * DPP-143 — public /report-link/:token page. A report email carries this link instead of the report attached:
 * no login, no code, one click to view and a Download button to keep a copy.
 *
 * An expired link says nothing about the report. The recipient is asked to contact their MMI team; MMI staff
 * open the report in Job Central, where it never expires.
 */
@Component({
  selector: 'app-report-link',
  templateUrl: './report-link.component.html',
  styleUrl: './report-link.component.scss',
  standalone: false
})
export class ReportLinkComponent implements OnInit {
  readonly STATUS = REPORT_LINK_STATUS;

  token = '';
  loading = true;
  loadError = false;
  report: ReportLinkViewModel | null = null;

  constructor(private route: ActivatedRoute, private exposeService: ExposeService,
    private spinner: SpinnerService, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.loadReport();
  }

  loadReport() {
    this.loading = true;
    this.spinner.show();
    this.exposeService.getReportLink(this.token).subscribe({
      next: (res: ReportLinkViewModel) => {
        this.spinner.hide();
        this.loading = false;
        this.report = res;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.loadError = true;
        console.error('[ReportLink] failed to load report link', err);
        this.cdr.detectChanges();
      }
    });
  }

  // MMI staff: the signed-in page that finds this report in Job Central (sign-in first if needed).
  staffLink(): string[] {
    return ['/show-job-central/report-link', this.token];
  }

  viewUrl(document: ReportLinkDocument): string {
    return this.exposeService.reportDocumentUrl(this.token, document.emailDocumentId, false);
  }

  downloadUrl(document: ReportLinkDocument): string {
    return this.exposeService.reportDocumentUrl(this.token, document.emailDocumentId, true);
  }

  // Only types the API serves inline get a View button; everything else is download-only.
  canView(document: ReportLinkDocument): boolean {
    return ['application/pdf', 'image/png', 'image/jpeg'].includes((document.contentType || '').toLowerCase());
  }

  fileSize(bytes: number): string {
    if (!bytes) {
      return '';
    }
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
